class WorkbookConfig {
    workbookId: KnockoutObservable<number>;
    workbookName: string;
    mappingId: number;
    mappingName: string;

    defaultWorkbook: KnockoutObservable<boolean>;
    defaultTabId: KnockoutObservable<number>;
    readonly userEditMode: WorkbookEditModeEnum;

    appBar: AppBarConfigVM;
    userDetails: UserDetailsVM;
    generalSettings: WorkbookGeneralSettings;
    preferences: WorkbookPreferences;

    tabs: KnockoutObservableArray<WorkbookTab>;
    activeTab: KnockoutObservable<WorkbookTab>;
    activeTabOrWidget: KnockoutObservable<ITabOrWidget>;
    filters: KnockoutObservableArray<Filter>;

    workbookHelper: WorkbookHelper;
    filterHelper: FilterHelper;

    ///Print and PDF
    isPrinting: boolean;
    isExcelPrinting: boolean;
    isEmailBody: boolean;
    suppressReportHeader: boolean;

    scheduledEmailId: number;

    /** flag to know that the workbook is being manually reloaded */
    reloadManually: boolean;

    /** Make knockout components to render again with the new tab or widget */
    isChangingTabOrWidget = ko.observable(false);

    private _reportTemplateList: KnockoutObservableArray<Drilldown>;

    constructor(data: any) {
        let self = this;

        this.workbookId = ko.observable(data.workbookId);
        this.workbookName = data.workbookName;
        this.mappingId = data.mappingId;
        this.mappingName = data.mappingName;
        this.defaultWorkbook = ko.observable(data.defaultWorkbook || false);
        this.defaultTabId = ko.observable(data.defaultTabId);
        this.userEditMode = data.userEditMode;

        this.appBar = new AppBarConfigVM(data.appBar)
        this.userDetails = new UserDetailsVM(data.userDetails);

        this.generalSettings = new WorkbookGeneralSettings();
        this.generalSettings.editing(isMobile() ? false : data.editing);
        this.preferences = new WorkbookPreferences(data.preferences, this);

        let tabs = createInstancesWithParameter(WorkbookTab, data.tabs as Array<any>, this);

        let tab = tabs.find(x => x.isActiveTab());
        if (!tab) {
            tab = tabs.find(x => x.isDefaultTab());
        }
        if (!tab) {
            tab = tabs[0];
        }

        if (!this.generalSettings.editing() && tab && tab.tabConfig.hiddenTab()) {
            tab = tabs.find(x => !x.tabConfig.hiddenTab());
        }

        this.activeTab = ko.observable(tab);
        this.activeTabOrWidget = ko.observable(tab);
        this.tabs = ko.observableArray(tabs);

        let filtersVM = createInstances(Filter, data.filters as Array<any>);
        this.filters = ko.observableArray(filtersVM);

        this.workbookHelper = new WorkbookHelper(null, this);
        this.filterHelper = new FilterHelper(this);

        this.activeTab.subscribe(function (newTab: WorkbookTab) {
            if (newTab.tabType() !== TabTypeEnum.Dashboard || !newTab.isDashboardLoaded || newTab.dashboard.selectedWidget() === null) {
                self.activeTabOrWidget(newTab);
            }
            else {
                self.activeTabOrWidget(newTab.dashboard.selectedWidget());
            }
        });
    }

    callIntroForEmptyWorkbook() {
        if (this.activeTab() && !Ir.Components.ReportDesigner.ReportDesigner.isReportDesignerOpen()) {
            var tourOpened = false;
            if (workbook.workbookConfig.generalSettings.editing()) {
                if (this.activeTab().tabType() !== TabTypeEnum.Dashboard) {
                    let reportConfig = this.activeTab().reportConfig;
                    if (reportConfig && reportConfig.isReportConfig) {
                        if (reportConfig.fields().length == 0) {
                            tourOpened = IntroService.callIntroForEmptyWorkbook();
                        }
                    } 
                }
                else {
                    if (this.activeTab().dashboard.widgets().length == 0) {
                        tourOpened = IntroService.callIntroForEmptyDashboard();
                    }
                }
            }
            if (!tourOpened) {
                IntroService.callIntroForIntro();
            }
        }
    }

    forceTabsRefresh() {
        for (let tab of this.tabs()) {
            if (tab.tabContent) {
                tab.forceRefresh(true);
            }
        }
    }
    get reportTemplateList() {
        if (!this._reportTemplateList) {
            let o = {
                verb: "GET",
                url: urlConfigs.tabs + "GetReportTemplates/" + this.workbookId(),
                dataType: "json",
                async: false
            } as RequestServerParams;            
            o.successFunction = function (data: any, e: Event) {
                let reportTemplates = createInstances(Drilldown, data);
                this._reportTemplateList = ko.observableArray(reportTemplates);
            }.bind(this);
            o.failureFunction = function (textStatus, errorThrown, errorItem: IError) {
                errorItem.userMessage = "Failed to get report templates";
                irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
            };

            irGlobal.requestServer(o);
        }
        return this._reportTemplateList;
    }

    getReportTemplateListForNewReport() {
        let reports = [{
            key: KeyReportStubTypeEnum.Design,
            items: [new ReportStub({
                id: NewReportStubTypeEnum.Report,
                name: "Design New Report",
                type: ReportTypeEnum.Analysis
            }), new ReportStub({
                id: NewReportStubTypeEnum.Chart,
                name: "Design New Chart",
                type: ReportTypeEnum.Chart
            }), new ReportStub({
                id: NewReportStubTypeEnum.Pivot,
                name: "Design New Pivot",
                type: ReportTypeEnum.Pivot
            }), new ReportStub({
                id: NewReportStubTypeEnum.Empty,
                name: "Create Empty Report",
                type: ReportTypeEnum.Analysis
            })]
        }];
        reports.push({ key: KeyReportStubTypeEnum.Templates, items: createInstances(ReportStub, this.reportTemplateList()) });
        return reports;
    }

    get hasPendingChanges() {
        let hasPendingChanges = false;

        let o = {
            url: urlConfigs.tabs + "HasPendingChanges",
            data: { workbookId: this.workbookId() },
            dataType: "json",
            async: false
        } as RequestServerParams;
        o.successFunction = function (this: WorkbookConfig, result, e) {
            hasPendingChanges = result.pendingChanges;
        }.bind(this);
        o.failureFunction = function (textStatus, errorThrown, errorItem: IError) {
            errorItem.userMessage = `Failed to get pending changes`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
        };

        irGlobal.requestServer(o);

        return hasPendingChanges;
    }

    get selectedDashboardWidget() {
        return this.activeTab().tabType() === TabTypeEnum.Dashboard && this.activeTab().isDashboardLoaded ? this.activeTab().dashboard.selectedWidget() : null;
    }

    exportToPDF(isLandscape: boolean, templateId: number) {
        let href = `/Email/Pdf?scope=Workbook&mappingId=${this.mappingId}&workbookId=${this.workbookId()}`;
        if (isLandscape) {
            href += `&landscape=${isLandscape}`;
        }

        if (templateId && templateId > 0) {
            href += `&pdfTemplateId=${templateId}`;
        }

        window.open(href, "_blank").focus();
    }

    setActiveTab(tabId: number) {
        let selectedIndexValue = workbook.workbookConfig.workbookHelper.workbookTabsDataSource().findIndex(x => x.tabId === tabId);
        workbook.workbookConfig.workbookHelper.workbookTabsSelectedIndex(selectedIndexValue);
    }

    collapseWidgets() {
        for (let tab of this.tabs()) {
            if (tab.tabType() === TabTypeEnum.Dashboard && tab.isDashboardLoaded) {
                for (let widget of tab.dashboard.widgets()) {
                    if (widget.expanded) {
                        widget.expandCollapse();
                    }
                }
            }
        }
    }

    delete() {
        let self = this;
        let result = DevExpress.ui.dialog.custom({
            title: this.workbookName,
            message: `Are you sure you want to delete the workbook?`,
            buttons: [
                {
                    text: "Yes",
                    type: 'danger',
                    onClick: function () { return true; }
                },
                {
                    text: "No",
                    onClick: function () { return false; }
                }
            ]
        });
        result.show().done(function (dialogResult) {
            if (dialogResult) {
                let o = {
                    url: urlConfigs.siteRoot + "Home/RemoveWorkbook",
                    data: { workbookId: self.workbookId() },
                    dataType: "json",
                    async: false
                } as RequestServerParams;
                o.successFunction = function () {
                    self.workbookHelper.goToURL(`${urlConfigs.siteRoot}home/home`, "home");
                }.bind(this);
                o.failureFunction = function (textStatus, errorThrown, errorItem: IError) {
                    errorItem.userMessage = `Failed to delete workbook`;
                    irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
                };

                irGlobal.requestServer(o);
            }
        });
    }
}